import { useRef, useState } from "react";
import { toast } from "sonner";
import { Globe, Upload, FileText, EyeOff, Sparkles } from "lucide-react";
import { onb, uploadDoc } from "@/lib/onb";
import { errText } from "@/lib/http";
import { Panel, Eyebrow } from "@/components/mh/bits";
import { Btn, Input } from "@/components/mh/controls";
import { ImportDrop } from "@/components/onb/ImportDrop";

export const Evidence = ({ st, reload, canEdit }) => {
  const [url, setUrl] = useState(st.profile.website || "");
  const [busy, setBusy] = useState(null);
  const [pct, setPct] = useState(0);
  const fileRef = useRef();
  const run = async (key, fn, ok) => { setBusy(key); try { await fn(); ok && toast.success(ok); await reload(); } catch (e) { toast.error(e.message && !e.response ? e.message : errText(e)); } setBusy(null); };
  const onFiles = (files) => run("upload", async () => { for (const f of files) { setPct(0); await uploadDoc(f, setPct); } }, "Document read");
  return (
    <div className="grid lg:grid-cols-[1.1fr_1fr] gap-4" data-testid="onb-evidence">
      <Panel cls="p-5 sm:p-6 space-y-5">
        <div><Eyebrow>Step 1 · Evidence</Eyebrow><h3 className="font-display text-3xl mt-1">Show us the business</h3>
          <p className="text-sm text-muted-foreground mt-1">We read your homepage plus up to 5 linked pages, and any documents you add. Only the text is used to tailor your roles.</p></div>
        <div className="flex gap-2 items-end">
          <div className="flex-1"><Input label="Company website" placeholder="acme.com" value={url} onChange={(e) => setUrl(e.target.value)} disabled={!canEdit} testId="onb-website-input" /></div>
          <Btn onClick={() => run("site", () => onb.website(url), "Website read")} disabled={!canEdit || !url || !!busy} testId="onb-website-btn"><Globe className="w-4 h-4" />{busy === "site" ? "Reading…" : "Read site"}</Btn>
        </div>
        <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); canEdit && onFiles([...e.dataTransfer.files]); }}
          onClick={() => canEdit && fileRef.current.click()} data-testid="onb-dropzone"
          className="rounded-xl border border-dashed border-white/15 hover:border-[#D4FF3A]/50 transition-colors p-6 text-center cursor-pointer">
          <Upload className="w-5 h-5 mx-auto text-muted-foreground" />
          <p className="text-sm mt-2">{busy === "upload" ? `Uploading and reading… ${pct}%` : "Drop playbooks, brand guides, SOPs, handbooks or price sheets"}</p>
          <p className="font-mono text-[10px] text-muted-foreground mt-1">PDF · DOCX · TXT · MD · up to 20 MB each</p>
          <input ref={fileRef} type="file" multiple accept=".pdf,.docx,.txt,.md,.csv" className="hidden" data-testid="onb-file-input" onChange={(e) => onFiles([...e.target.files])} />
        </div>
        <ul className="divide-y divide-white/[0.05]" data-testid="onb-sources">
          {st.sources.map((s) => (
            <li key={s.id} className="py-2 flex items-center gap-3 text-sm">
              {s.kind === "website" ? <Globe className="w-4 h-4 text-[#00E5FF]" /> : <FileText className="w-4 h-4 text-[#D4FF3A]" />}
              <span className="flex-1 truncate">{s.title}</span><span className="font-mono text-[10px] text-muted-foreground">{(s.chars || 0).toLocaleString()} chars</span>
              {canEdit && <button onClick={() => run("hide", () => onb.hideDoc(s.id))} className="text-muted-foreground hover:text-foreground" aria-label="Hide" data-testid={`onb-hide-${s.id}`}><EyeOff className="w-4 h-4" /></button>}
            </li>
          ))}
          {st.sources.length === 0 && <li className="py-3 text-sm text-muted-foreground">Nothing added yet.</li>}
        </ul>
      </Panel>
      <Panel cls="p-5 sm:p-6 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div><Eyebrow>Company picture</Eyebrow><h3 className="font-display text-2xl mt-1">What we understood</h3></div>
          <Btn variant="primary" onClick={() => run("draft", onb.draft, "Draft ready, so check it below")} disabled={!canEdit || !st.sources.length || !!busy} testId="onb-draft-btn"><Sparkles className="w-4 h-4" />{busy === "draft" ? "Drafting…" : "Draft it"}</Btn>
        </div>
        <CompanyPicture st={st} />
        {Object.keys(st.profile.company || {}).length > 0 && (
          <Btn onClick={() => run("confirm", onb.confirm, "Company picture confirmed")} disabled={!canEdit || st.profile.draft_confirmed} testId="onb-confirm-btn">
            {st.profile.draft_confirmed ? "Confirmed ✓" : "Looks right, confirm"}</Btn>
        )}
      </Panel>
      <div className="lg:col-span-2">
        <ImportDrop reload={reload} canEdit={canEdit} />
      </div>
    </div>
  );
};

const PICTURE = ["summary", "offer", "icp", "positioning", "voice", "tools", "policies"];
const CompanyPicture = ({ st }) => {
  const c = st.profile.company || {};
  if (!PICTURE.some((k) => c[k])) return <p className="text-sm text-muted-foreground">Add sources, then press Draft it. You can correct anything on the Profile tab.</p>;
  return (
    <dl className="space-y-2.5" data-testid="onb-picture">
      {PICTURE.filter((k) => c[k]).map((k) => (
        <div key={k}><dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{k} <span className="normal-case tracking-normal text-[#00E5FF]/70">· {c[k].source}</span></dt>
          <dd className="text-sm mt-0.5">{Array.isArray(c[k].value) ? c[k].value.join(" · ") : String(c[k].value)}</dd></div>
      ))}
    </dl>
  );
};
